import {
  buildSplineSegmentCurve,
  isSmoothSplineArrival,
  splineSegmentKnots,
} from '@datum-sdk/kernel';
import { isViewWaypoint } from '@datum-sdk/scene-format';
import type { CameraRoute, RouteWaypoint, ScenePoint } from '@datum-sdk/scene-format';

type Point = ScenePoint;
type Bezier = [number, number, number, number];

const isPoint = (value: unknown): value is Point => Array.isArray(value) && value.length === 3;

/** A segment shorter than this flies nowhere, so it has no speed to match. */
const NO_TRAVEL = 1e-9;
/** Two speeds this close at one knot are the same speed, written twice. */
const SAME_SPEED = 1e-6;

/**
 * Where the segment arriving at each waypoint departs from — the position the
 * previous waypoint left the camera at — and where the route lands.
 *
 * `entry` is the camera's position when the route starts. A waypoint with no
 * position of its own (a hold, a turn) leaves the camera where it was. An orbit
 * or a POI-bound waypoint is resolved by the engine at playback, so what
 * follows it is unknown here.
 */
export const departurePositions = (
  waypoints: readonly RouteWaypoint[],
  entry?: Point,
): { starts: (Point | undefined)[]; landing: Point | undefined } => {
  const starts: (Point | undefined)[] = [];
  let cursor = entry;
  for (const waypoint of waypoints) {
    starts.push(cursor);
    if (isViewWaypoint(waypoint)) cursor = waypoint.position;
    else if (waypoint.orbit || waypoint.poiId) cursor = undefined;
    else if (isPoint(waypoint.target)) cursor = waypoint.target;
  }
  return { starts, landing: cursor };
};

/**
 * Length of every smooth segment as the SDK flies it — the kernel's own curve
 * through the same knots — index-aligned with the route; `null` where the
 * segment is not smooth or its start cannot be resolved.
 */
export const smoothSegmentLengths = (route: CameraRoute, entry?: Point): (number | null)[] => {
  const { starts } = departurePositions(route.waypoints, entry);
  const options = { stepTransition: route.stepTransition };
  return route.waypoints.map((_, index) => {
    const knots = splineSegmentKnots(route.waypoints, index, starts, options);
    return knots ? buildSplineSegmentCurve(knots).length : null;
  });
};

/**
 * The bezier of a segment that leaves at `startSpeed` and arrives at `endSpeed`
 * (world units per ms). x-controls at 1/3 and 2/3 make x(s) = s, so its y IS
 * the cubic Hermite through those speeds — exact, not fitted.
 */
export const hermiteEasing = (
  startSpeed: number,
  endSpeed: number,
  length: number,
  duration: number,
): Bezier => {
  const scale = duration / (3 * length);
  return [1 / 3, startSpeed * scale, 2 / 3, 1 - endSpeed * scale];
};

/**
 * Restore speed continuity across every smooth run after an edit.
 *
 * A view's cubic-bezier is derived data: it encodes the speed at both ends of
 * its segment relative to that segment's length, so moving one view in the
 * editor changes two lengths and the speeds the two sides of a knot ask for no
 * longer agree. This reads, against today's geometry, the speed each bezier
 * asks for at each end, and:
 *
 * - keeps a knot where both sides still agree — the pacing the converter wrote
 *   from the legacy curve survives untouched, so running this on unedited data
 *   changes nothing;
 * - meets in the middle where they do not, which keeps the tempo the author had
 *   while making it continuous;
 * - holds the run at rest at both ends;
 * - never lets a knot run faster than three times either neighbouring
 *   segment's mean speed (Fritsch–Carlson), so no segment overshoots and flies
 *   back.
 *
 * Durations are kept. `entry` is where the camera stands when the route starts;
 * the flyover needs it, since its run opens behind a bare hold. A run whose
 * start cannot be resolved, or with a view timed by the run's pooled budget
 * rather than its own `duration`, keeps its easing; a preset easing reads as
 * linear.
 */
export const repaceSmoothRuns = (route: CameraRoute, entry?: Point): RouteWaypoint[] => {
  const waypoints = [...route.waypoints];
  // ponytail: a looping route's closing leg would need the cyclic knot reading;
  // no looping route has a smooth run yet.
  if (route.loop) return waypoints;
  const lengths = smoothSegmentLengths(route, entry);

  let first = 0;
  while (first < waypoints.length) {
    if (lengths[first] === null) {
      first++;
      continue;
    }
    let last = first;
    while (last + 1 < waypoints.length && lengths[last + 1] !== null) last++;
    // A run continuing one whose start is unknown is only its tail.
    const truncated =
      first > 0 && isSmoothSplineArrival(waypoints[first - 1], route.stepTransition);
    if (!truncated) repaceRun(waypoints, lengths as number[], first, last);
    first = last + 1;
  }
  return waypoints;
};

/** The speed (units per ms) a segment's easing asks for at each end: the bezier's end slopes. */
const askedSpeeds = (
  easing: RouteWaypoint['easing'],
  length: number,
  duration: number,
): { start: number; end: number } => {
  const mean = length / duration;
  // ponytail: a preset reads as linear; presets other than 'linear' do not occur in a run yet.
  if (!Array.isArray(easing)) return { start: mean, end: mean };
  const [x1, y1, x2, y2] = easing as Bezier;
  return {
    start: x1 > 0 ? (y1 / x1) * mean : mean,
    end: x2 < 1 ? ((1 - y2) / (1 - x2)) * mean : mean,
  };
};

const repaceRun = (
  waypoints: RouteWaypoint[],
  lengths: readonly number[],
  first: number,
  last: number,
): void => {
  const segments: { length: number; duration: number; asked: { start: number; end: number } }[] =
    [];
  for (let index = first; index <= last; index++) {
    const { duration, speed, easing } = waypoints[index];
    if (duration === undefined || duration <= 0 || speed !== undefined) return;
    const length = lengths[index];
    segments.push({ length, duration, asked: askedSpeeds(easing, length, duration) });
  }

  const meanSpeed = (segment: number): number =>
    segments[segment].length / segments[segment].duration;
  const knotSpeeds = [0];
  for (let knot = 1; knot < segments.length; knot++) {
    const left = segments[knot - 1].asked.end;
    const right = segments[knot].asked.start;
    const agreed =
      Math.abs(left - right) <= SAME_SPEED * Math.max(left, right) ? right : (left + right) / 2;
    knotSpeeds.push(Math.max(0, Math.min(agreed, 3 * meanSpeed(knot - 1), 3 * meanSpeed(knot))));
  }
  knotSpeeds.push(0);

  segments.forEach(({ length, duration }, segment) => {
    if (length <= NO_TRAVEL) return;
    waypoints[first + segment] = {
      ...waypoints[first + segment],
      easing: hermiteEasing(knotSpeeds[segment], knotSpeeds[segment + 1], length, duration),
    };
  });
};
